/**
 * EmailModeSettings — admin-controlled 3-way toggle for the email
 * delivery system (Automatic / Manual / Disabled). Backed by:
 *   GET  /api/admin/settings/email-mode
 *   PUT  /api/admin/settings/email-mode  { mode }
 *
 * The persisted setting is read by the SendGrid safety-net wrapper on
 * every send, so a mode change takes effect immediately without a
 * backend restart.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CheckCircle2, ShieldAlert, PowerOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_ICONS = {
  automatic: Mail,
  manual: ShieldAlert,
  disabled: PowerOff,
};

const MODE_TONE = {
  automatic: 'text-amber-500 border-amber-500/40 bg-amber-500/10',
  manual: 'text-emerald-500 border-emerald-500/40 bg-emerald-500/10',
  disabled: 'text-rose-500 border-rose-500/40 bg-rose-500/10',
};

export default function EmailModeSettings() {
  const [current, setCurrent] = useState(null);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/settings/email-mode`);
        if (!alive) return;
        setCurrent(data.mode);
        setSelected(data.mode);
        setOptions(data.options || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load email mode');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    if (!selected || selected === current) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/email-mode`, { mode: selected });
      setCurrent(selected);
      const label = (options.find((o) => o.value === selected) || {}).label || selected;
      toast.success(`Email mode changed to "${label}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border rounded-sm max-w-3xl" data-testid="email-mode-card">
      <CardHeader className="border-b border-border pb-4">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Email Notification Mode
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Controls whether operational admin-action emails are auto-sent to
          staff. Individual system emails (invitation, doc decision, account
          status, shift claim) always fire regardless of this setting.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground italic">Loading…</p>
        ) : (
          <>
            {options.map((opt) => {
              const Icon = MODE_ICONS[opt.value] || Mail;
              const isSelected = selected === opt.value;
              const isCurrent = current === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelected(opt.value)}
                  className={
                    'w-full text-left rounded-sm border p-3 transition-colors ' +
                    (isSelected
                      ? MODE_TONE[opt.value]
                      : 'border-border hover:bg-accent/40')
                  }
                  data-testid={`email-mode-option-${opt.value}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.8} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium uppercase tracking-wider">{opt.label}</p>
                        {isCurrent ? (
                          <Badge className="bg-primary/15 text-primary border-primary/40 text-[9px] uppercase tracking-wider">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Current
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{opt.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="pt-3 border-t border-border flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">
                Changes take effect immediately for all outgoing emails.
              </p>
              <Button
                size="sm"
                disabled={saving || !selected || selected === current}
                onClick={handleSave}
                className="rounded-sm uppercase tracking-wider text-xs h-8"
                data-testid="email-mode-save-btn"
              >
                {saving ? 'Saving…' : 'Save Mode'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
